import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Compass, Check, X, AlertTriangle, Sun } from 'lucide-react';
import { ouvrirFenetre, fermerFenetre } from '../utils/pauseGlobale';

const API = process.env.REACT_APP_BACKEND_URL || '';

const nomOrientation = (d) => {
  const noms = ['Nord', 'Nord-Est', 'Est', 'Sud-Est', 'Sud', 'Sud-Ouest', 'Ouest', 'Nord-Ouest'];
  return noms[Math.round((d % 360) / 45) % 8];
};

const estFavorable = (d) => {
  const a = d % 360;
  return a >= 78.75 && a < 281.25;
};

const BoussoleToiture = ({ chantierId, orientation, onEnregistre }) => {
  const [ouvert, setOuvert] = useState(false);
  const [cap, setCap] = useState(null);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [autorise, setAutorise] = useState(false);
  const ecouteur = useRef(null);
  const angleContinu = useRef(0);
  const [affichage, setAffichage] = useState(0);

  const demarrer = async () => {
    setErreur('');
    ouvrirFenetre();
    setOuvert(true);

    const lire = (e) => {
      let d = null;
      if (e.webkitCompassHeading != null) {
        d = e.webkitCompassHeading;
      } else if (e.alpha != null) {
        d = e.absolute ? (360 - e.alpha) % 360 : (360 - e.alpha) % 360;
      }
      if (d != null && !isNaN(d)) {
        const arrondi = Math.round(d);
        setCap(arrondi);
        setAutorise(true);
        const actuel = angleContinu.current;
        const ecart = ((arrondi - (actuel % 360)) + 540) % 360 - 180;
        angleContinu.current = actuel + ecart;
        setAffichage(angleContinu.current);
      }
    };

    try {
      if (typeof DeviceOrientationEvent !== 'undefined' &&
          typeof DeviceOrientationEvent.requestPermission === 'function') {
        const rep = await DeviceOrientationEvent.requestPermission();
        if (rep !== 'granted') {
          setErreur("Autorisation refusée. Activez la boussole dans les réglages du navigateur.");
          return;
        }
      }
      ecouteur.current = lire;
      window.addEventListener('deviceorientationabsolute', lire, true);
      window.addEventListener('deviceorientation', lire, true);

      setTimeout(() => {
        setAutorise((a) => {
          if (!a) setErreur("La boussole ne répond pas. Vérifiez que votre appareil la prend en charge.");
          return a;
        });
      }, 3000);
    } catch (e) {
      setErreur("Impossible d'accéder à la boussole");
    }
  };

  const fermer = () => {
    fermerFenetre();
    if (ecouteur.current) {
      window.removeEventListener('deviceorientationabsolute', ecouteur.current, true);
      window.removeEventListener('deviceorientation', ecouteur.current, true);
      ecouteur.current = null;
    }
    setOuvert(false);
    setCap(null);
    setAutorise(false);
    setErreur('');
  };

  useEffect(() => () => {
    if (ecouteur.current) {
      window.removeEventListener('deviceorientationabsolute', ecouteur.current, true);
      window.removeEventListener('deviceorientation', ecouteur.current, true);
    }
  }, []);

  const valider = async () => {
    if (cap == null) return;
    setEnCours(true);
    try {
      const r = await axios.post(`${API}/api/chantiers/${chantierId}/orientation`, { degres: cap });
      fermer();
      if (onEnregistre) onEnregistre(r.data.data);
    } catch (e) {
      setErreur("Échec de l'enregistrement");
    } finally {
      setEnCours(false);
    }
  };

  const carte = { background: '#fff', border: '1px solid #F3F4F6', borderRadius: '12px', padding: '17px', marginBottom: '14px' };

  if (orientation && !ouvert) {
    return (
      <div style={{ ...carte, borderColor: orientation.favorable ? '#F3F4F6' : '#FCA5A5' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '9px' }}>
          {orientation.favorable ? <Sun size={18} color="#10B981" /> : <AlertTriangle size={18} color="#DC2626" />}
          <span style={{ fontSize: '15px', fontWeight: 500 }}>Orientation de la toiture</span>
        </div>
        <div style={{ fontSize: '22px', fontWeight: 600, color: orientation.favorable ? '#065F46' : '#991B1B', marginBottom: '4px' }}>
          {orientation.libelle}
        </div>
        <div style={{ fontSize: '13px', color: '#6B7280' }}>
          {orientation.degres}° · mesuré par {orientation.par}
        </div>
        {!orientation.favorable && (
          <div style={{ marginTop: '12px', padding: '12px', background: '#FEE2E2', borderRadius: '8px', fontSize: '13px', color: '#991B1B', lineHeight: 1.5 }}>
            Cette orientation ne permet pas un rendement optimal. Contactez l'administrateur avant de poursuivre.
          </div>
        )}
        <button onClick={demarrer}
          style={{ width: '100%', marginTop: '13px', padding: '11px', border: '1px solid #E5E7EB', borderRadius: '9px', background: '#fff', color: '#6B7280', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          <Compass size={15} /> Refaire la mesure
        </button>
      </div>
    );
  }

  return (
    <>
      <div style={carte}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '6px' }}>
          <Compass size={18} color="#10B981" />
          <span style={{ fontSize: '15px', fontWeight: 500 }}>Orientation de la toiture</span>
        </div>
        <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px' }}>
          À mesurer avant l'installation
        </div>
        <div style={{ display: 'flex', gap: '9px', padding: '12px', background: '#FFFBEB', borderRadius: '9px', marginBottom: '14px' }}>
          <AlertTriangle size={16} color="#92400E" style={{ flexShrink: 0, marginTop: '1px' }} />
          <span style={{ fontSize: '12.5px', color: '#92400E', lineHeight: 1.5 }}>
            La mesure doit être effectuée <strong>en présence du client</strong> et validée avec lui avant de poursuivre.
          </span>
        </div>
        <button onClick={demarrer}
          style={{ width: '100%', padding: '13px', border: 'none', borderRadius: '9px', background: '#10B981', color: '#fff', fontSize: '15px', cursor: 'pointer' }}>
          Mesurer l'orientation
        </button>
      </div>

      {ouvert && (
        <div style={{ position: 'fixed', inset: 0, background: '#111827', zIndex: 200, display: 'flex', flexDirection: 'column', color: '#fff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px' }}>
            <span style={{ fontSize: '15px' }}>Orientation de la toiture</span>
            <X size={24} style={{ cursor: 'pointer' }} onClick={fermer} />
          </div>

          <div style={{ padding: '0 24px 20px', fontSize: '14px', color: 'rgba(255,255,255,0.8)', lineHeight: 1.55, textAlign: 'center' }}>
            Placez-vous <strong style={{ color: '#fff' }}>dos à la toiture</strong>, en regardant dans la direction de la pente.
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            {erreur ? (
              <div style={{ textAlign: 'center', color: '#FCA5A5', fontSize: '15px', lineHeight: 1.5, padding: '0 20px' }}>
                {erreur}
              </div>
            ) : cap == null ? (
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '15px' }}>Recherche du nord…</div>
            ) : (
              <>
                <div style={{ position: 'relative', width: '230px', height: '230px', marginBottom: '26px' }}>
                  <svg viewBox="0 0 230 230" style={{ width: '100%', height: '100%', transform: `rotate(${-affichage}deg)`, transition: 'transform .25s cubic-bezier(0.25, 0.8, 0.4, 1)' }}>
                    <circle cx="115" cy="115" r="105" fill="none" stroke="rgba(255,255,255,0.16)" strokeWidth="2" />
                    <path d="M 115 22 L 128 60 L 115 52 L 102 60 Z" fill="#DC2626" />
                    <text x="115" y="16" textAnchor="middle" fontSize="15" fill="#DC2626" fontWeight="600">N</text>
                    <text x="222" y="121" textAnchor="middle" fontSize="14" fill="rgba(255,255,255,0.5)">E</text>
                    <text x="115" y="226" textAnchor="middle" fontSize="15" fill="#10B981" fontWeight="600">S</text>
                    <text x="8" y="121" textAnchor="middle" fontSize="14" fill="rgba(255,255,255,0.5)">O</text>
                    <path d="M 78 190 A 105 105 0 0 0 152 190 A 105 105 0 0 0 78 190" fill="rgba(16,185,129,0.12)" />
                  </svg>
                  <div style={{ position: 'absolute', top: 0, left: '50%', width: '2px', height: '30px', background: '#10B981', transform: 'translateX(-50%)' }} />
                </div>

                <div style={{ fontSize: '34px', fontWeight: 600, marginBottom: '5px', color: estFavorable(cap) ? '#6EE7B7' : '#FCA5A5' }}>
                  {nomOrientation(cap)}
                </div>
                <div style={{ fontSize: '17px', color: 'rgba(255,255,255,0.6)', marginBottom: '18px' }}>{cap}°</div>

                {!estFavorable(cap) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '9px', background: 'rgba(220,38,38,0.18)', padding: '13px 16px', borderRadius: '10px', fontSize: '13.5px', color: '#FCA5A5', maxWidth: '320px', lineHeight: 1.45 }}>
                    <AlertTriangle size={17} style={{ flexShrink: 0 }} />
                    Orientation défavorable au rendement solaire
                  </div>
                )}
              </>
            )}
          </div>

          <div style={{ padding: '16px 20px 28px' }}>
            <button onClick={valider} disabled={cap == null || enCours}
              style={{
                width: '100%', padding: '16px', border: 'none', borderRadius: '10px',
                background: cap == null ? 'rgba(255,255,255,0.12)' : '#10B981',
                color: cap == null ? 'rgba(255,255,255,0.4)' : '#fff',
                fontSize: '16px', cursor: cap == null ? 'default' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '9px'
              }}>
              <Check size={18} /> {enCours ? 'Enregistrement…' : "Valider l'orientation"}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default BoussoleToiture;
